import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  Row,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FloatInput, showToast, WizardFooter } from "src/componentes";
import { MenuDropdownToggle } from "src/componentes/MenuDropdownToggle";
import {
  formatarPayloadMDFe,
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  updateLocal,
} from "../MDFe.Dto";
import { useNavigate } from "react-router-dom";
import {
  MDFeExcluir,
  MDFeFiltrar,
  MDFeIncluir,
} from "src/services/Fiscal.Service";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import moment from "moment";
import { showMessageBox } from "src/componentes/MessageBox/Messagebox";
import {
  FinalizacaoFormType,
  FinalizacaoSchema,
  validarMDFeInclusao,
} from "./MDFe.Cadastro.Dto";
import MDFeConfiguracaoCode from "../MDFe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "src/pages/Privada/Configuracoes/Sistema/Sistema.Dto";

const MDFeCadastroFinalizacaoView = () => {
  const navigate = useNavigate();
  const configuracao = MDFeConfiguracaoCode();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);

  // Atribui a série automaticamente com base na configuração carregada caso ainda não definida
  useEffect(() => {
    if (!configuracao.loading) {
      const serieValor = configuracao.getConfiguracaoValor(
        TipoConfiguracaoEnum.Serie
      );
      const serieNum = Number(serieValor);
      if (serieNum > 0) {
        const mdfeAtual = getLocal<MDFeInput>(STORAGEMDFE_KEY);
        const isEdicao = Boolean(mdfeAtual?.id && mdfeAtual.id > 0);
        if (!isEdicao || !mdfeAtual?.serie) {
          updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
            serie: serieNum,
          });
        }
      }
    }
  }, [configuracao.loading]);

  const [numeroMDFe, setNumeroMDFe] = useState<number | undefined>(
    mdfe?.numeroMDFe && mdfe.numeroMDFe > 0 ? mdfe.numeroMDFe : undefined
  );

  const form = useForm<FinalizacaoFormType>({
    resolver: yupResolver(FinalizacaoSchema),
    defaultValues: {
      emissaoDataHora: mdfe?.emissaoDataHora
        ? moment(mdfe.emissaoDataHora).format("DD/MM/YYYY HH:mm:ss")
        : moment().format("DD/MM/YYYY HH:mm:ss"),
      informacoesAdicionaisFisco: mdfe?.informacoesAdicionaisFisco ?? "",
      informacoesComplementaresContribuinte:
        mdfe?.informacoesComplementaresContribuinte ?? "",
    },
  });

  const handleVoltar = () => {
    navigate("../contratantes");
  };

  const handleAlterarNumeroMDFe = () => {
    const input = prompt(
      "Informe o número do MDF-e (deixe em branco para sequência automática):",
      numeroMDFe ? String(numeroMDFe) : ""
    );

    if (input === null) return;

    const trimmed = input.trim();
    if (!trimmed) {
      setNumeroMDFe(undefined);
      updateLocal<MDFeInput>(STORAGEMDFE_KEY, { numeroMDFe: 0 });
      showToast({
        message: "A numeração do MDF-e seguirá a sequência automática.",
        title: "Número do MDF-e",
        variant: "Info",
      });
      return;
    }

    const valor = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || isNaN(valor) || valor <= 0) {
      showToast({
        message:
          "Número de MDF-e inválido! Por favor, digite um número inteiro positivo.",
        title: "Validação",
        variant: "Danger",
      });
      return;
    }

    setNumeroMDFe(valor);
    updateLocal<MDFeInput>(STORAGEMDFE_KEY, { numeroMDFe: valor });
    showToast({
      message: `Será atribuído o número ${valor} para o MDF-e.`,
      title: "Número do MDF-e",
      variant: "Success",
    });
  };

  const handleRemoverNumeroMDFe = () => {
    setNumeroMDFe(undefined);
    updateLocal<MDFeInput>(STORAGEMDFE_KEY, { numeroMDFe: 0 });
    showToast({
      message: "A numeração do MDF-e seguirá a sequência automática.",
      title: "Número do MDF-e",
      variant: "Info",
    });
  };

  const handleConfirmar = async (data: FinalizacaoFormType) => {
    const formattedEmissao = moment(
      data.emissaoDataHora,
      "DD/MM/YYYY HH:mm:ss"
    ).format("YYYY-MM-DDTHH:mm:ss");

    const finalNumeroMDFe = numeroMDFe ? Number(numeroMDFe) : 0;
    const serieConfig = Number(
      configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.Serie)
    );
    const finalSerie =
      mdfe?.serie && mdfe.serie > 0
        ? mdfe.serie
        : serieConfig > 0
        ? serieConfig
        : 1;

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      emissaoDataHora: formattedEmissao,
      serie: finalSerie,
      numeroMDFe: finalNumeroMDFe,
      informacoesAdicionaisFisco: data.informacoesAdicionaisFisco || undefined,
      informacoesComplementaresContribuinte:
        data.informacoesComplementaresContribuinte || undefined,
    });

    const mdfeFinal = getLocal<MDFeInput>(STORAGEMDFE_KEY);
    if (!mdfeFinal) {
      showToast({
        message: "Dados do MDF-e não encontrados no armazenamento",
        title: "Erro",
        variant: "Danger",
      });
      return;
    }

    showToast({
      message: "Processando inclusão do MDF-e...",
      title: "Em cadastro",
      variant: "Info",
    });

    if (mdfeFinal.id && mdfeFinal.id > 0) {
      try {
        const mdfeExiste = await MDFeFiltrar({ id: mdfeFinal.id });
        if (mdfeExiste.data.success && mdfeExiste.data.data.length > 0) {
          const responseExclusao = await MDFeExcluir(mdfeFinal.id);
          if (!responseExclusao.data.success) {
            MapearErrosServidor(responseExclusao.data.notifications, showToast);
            return;
          }
        }
      } catch (error) {
        console.error("Erro ao verificar/substituir MDF-e existente:", error);
      }
    }

    try {
      const payloadFinal = formatarPayloadMDFe(mdfeFinal);

      // Validação consolidada da inclusão do MDF-e baseada no MDFeInputValidator do backend
      const validacao = await validarMDFeInclusao(payloadFinal);
      if (!validacao.isValid) {
        validacao.errors.forEach((msg) => {
          showToast({
            message: msg,
            title: "Validação do MDF-e",
            variant: "Danger",
          });
        });
        return;
      }

      const response = await MDFeIncluir(payloadFinal);

      if (response.data.success) {
        showToast({
          message: "MDF-e incluído com sucesso!",
          title: "Sucesso",
          variant: "Success",
        });
        updateLocal<MDFeInput>(STORAGEMDFE_KEY, null);
        navigate("/fiscal/mdfe");
      } else {
        const notifications = response.data.notifications || [];
        MapearErrosServidorCampo(notifications, form.setError);
        notifications.forEach((item: any) => {
          showToast({
            message: `${item.key ? item.key + ": " : ""}${item.message}`,
            title: "Erro",
            variant: "Danger",
          });
        });
      }
    } catch (error) {
      console.error("Erro ao incluir MDF-e:", error);
      showToast({
        message: "Ocorreu erro ao comunicar com o servidor",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const onSubmit = async (data: FinalizacaoFormType) => {
    const mensagemConfirmacao = numeroMDFe
      ? `Deseja confirmar o MDF-e com o número ${numeroMDFe}?`
      : "Deseja confirmar a emissão do MDF-e?";

    showMessageBox(
      mensagemConfirmacao,
      "Atenção!",
      "YesNo",
      "Question",
      {
        onYes: async () => {
          await handleConfirmar(data);
        },
      }
    );
  };

  const onError = (errors: any) => {
    if (errors.emissaoDataHora) {
      showToast({
        message: "A Data e Hora de Emissão é obrigatória e deve ser válida.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (
      errors.informacoesAdicionaisFisco ||
      errors.informacoesComplementaresContribuinte
    ) {
      showToast({
        message:
          "As informações adicionais excedem o limite máximo de caracteres permitido.",
        title: "Validação",
        variant: "Warning",
      });
      return;
    }
    showToast({
      message: "Existem campos obrigatórios não preenchidos na finalização.",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <h5 className="mb-0 text-primary">Finalização e Emissão do MDF-e</h5>
        </Col>
        <Col xs="auto">
          <Dropdown align="end">
            <MenuDropdownToggle>
              <FontAwesomeIcon icon={faEllipsisV} />
            </MenuDropdownToggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleAlterarNumeroMDFe}>
                Alterar número do MDF-e
              </Dropdown.Item>
              {numeroMDFe && numeroMDFe > 0 ? (
                <Dropdown.Item onClick={handleRemoverNumeroMDFe}>
                  Usar sequência automática
                </Dropdown.Item>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      {numeroMDFe && numeroMDFe > 0 ? (
        <Row className="mb-3">
          <Col xs={12}>
            <Alert
              variant="info"
              className="d-flex align-items-center py-2 mb-0"
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              <span>
                Número do MDF-e vinculado: <strong>{numeroMDFe}</strong> (será
                atribuído ao documento)
              </span>
            </Alert>
          </Col>
        </Row>
      ) : null}

      <Row className="g-3">
        <Col xs={12}>
          <FloatInput
            label="Data e Hora de Emissão"
            inputMask="datahora"
            registerField={form.register("emissaoDataHora")}
            errorsMessage={form.formState.errors.emissaoDataHora}
          />
        </Col>
        <Col xs={12}>
          <FloatInput
            label="Informações Adicionais de Interesse do Fisco"
            inputMask="textarea"
            maxLength={2000}
            registerField={form.register("informacoesAdicionaisFisco")}
            errorsMessage={form.formState.errors.informacoesAdicionaisFisco}
          />
        </Col>
        <Col xs={12}>
          <FloatInput
            label="Informações Complementares de Interesse do Contribuinte"
            inputMask="textarea"
            maxLength={5000}
            registerField={form.register(
              "informacoesComplementaresContribuinte"
            )}
            errorsMessage={
              form.formState.errors.informacoesComplementaresContribuinte
            }
          />
        </Col>
      </Row>

      <WizardFooter
        onVoltar={handleVoltar}
        proximoVariant="success"
        proximoLabel="Confirmar e Salvar MDF-e"
      />
    </Form>
  );
};

export default MDFeCadastroFinalizacaoView;
